import { useState, useRef, useEffect } from 'react';
import {
  Download, Share2, Copy, FileText, File as FileIcon, Play, Music,
  ChevronLeft, ChevronRight, X, ExternalLink, MoreVertical,
} from 'lucide-react';
import { toast } from 'sonner';

/** Convert API-relative URLs to absolute ones so download/share always work. */
function resolveUrl(u) {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${u.startsWith('/') ? '' : '/'}${u}`;
}

function getFilename(url) {
  try {
    const u = url.split('?')[0];
    return decodeURIComponent(u.split('/').pop() || 'file');
  } catch { return 'file'; }
}

function getExt(url) {
  const name = getFilename(url);
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg', 'bmp']);
const VIDEO_EXTS = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv', 'avi', 'wmv']);
const AUDIO_EXTS = new Set(['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a']);
const SOURCE_EXTS = new Set(['ai', 'psd', 'eps', 'fig', 'sketch', 'xd', 'zip', 'rar', '7z', 'indd', 'aep', 'prproj']);

function typeOf(url) {
  const e = getExt(url);
  if (IMAGE_EXTS.has(e)) return 'image';
  if (VIDEO_EXTS.has(e)) return 'video';
  if (AUDIO_EXTS.has(e)) return 'audio';
  if (e === 'pdf') return 'pdf';
  if (SOURCE_EXTS.has(e)) return 'source';
  return 'file';
}

/** Tiny size pill formatter — accepts raw URL; if we have no metadata, we hide it. */
function sizeLabel(meta) {
  const bytes = meta?.size;
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function DownloadMenu({ url, filename, compact = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const triggerDownload = () => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setOpen(false);
    toast.success(`Downloading ${filename}`);
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ url, title: filename }); }
      catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
    setOpen(false);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied');
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`flex items-center gap-1 rounded-lg text-white transition-all ${compact
          ? 'px-2 py-1 bg-black/50 hover:bg-black/70 backdrop-blur-md text-[11px]'
          : 'px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-xs font-semibold'}`}
        aria-label="Download or share"
        data-testid="file-download-menu-trigger"
      >
        <Download size={compact ? 12 : 14} />
        {!compact && <span>Download</span>}
        <MoreVertical size={compact ? 11 : 12} className="opacity-60" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 rounded-xl bg-[#1A1B3A] border border-white/15 shadow-[0_18px_40px_-10px_rgba(0,0,0,0.7)] py-1.5 z-30">
          <button onClick={triggerDownload} className="w-full text-left px-3 py-2 hover:bg-white/5 text-white text-xs flex items-center gap-2 font-semibold" data-testid="file-action-download">
            <Download size={13} /> Download
          </button>
          <button onClick={share} className="w-full text-left px-3 py-2 hover:bg-white/5 text-white text-xs flex items-center gap-2 font-semibold" data-testid="file-action-share">
            <Share2 size={13} /> Share
          </button>
          <button onClick={copyLink} className="w-full text-left px-3 py-2 hover:bg-white/5 text-white text-xs flex items-center gap-2 font-semibold" data-testid="file-action-copy">
            <Copy size={13} /> Copy link
          </button>
          <a href={url} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="w-full text-left px-3 py-2 hover:bg-white/5 text-white text-xs flex items-center gap-2 font-semibold">
            <ExternalLink size={13} /> Open in new tab
          </a>
        </div>
      )}
    </div>
  );
}

function Thumbnail({ file, onClick }) {
  const t = typeOf(file.url);
  const size = sizeLabel(file);
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
  };
  return (
    <div
      onClick={onClick}
      onKeyDown={onKey}
      role="button"
      tabIndex={0}
      className="group relative shrink-0 w-[180px] sm:w-[220px] aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-fuchsia-400/60 bg-[#0E0E23] transition-all text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-fuchsia-400/50"
      data-testid="file-gallery-thumb"
    >
      {t === 'image' && (
        <img src={file.url} alt={file.name} className="w-full h-full object-cover" loading="lazy" />
      )}
      {t === 'video' && (
        <>
          <video src={file.url} className="w-full h-full object-cover" preload="metadata" muted playsInline />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
              <Play size={22} className="text-[#0E0E23] ml-1" fill="#0E0E23" />
            </div>
          </div>
        </>
      )}
      {t === 'audio' && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-fuchsia-500/20 to-violet-500/10">
          <Music size={42} className="text-fuchsia-200" />
          <p className="mt-2 text-white text-xs font-bold uppercase tracking-wider">Audio</p>
        </div>
      )}
      {t === 'pdf' && (
        <embed src={`${file.url}#toolbar=0&navpanes=0`} type="application/pdf" className="w-full h-full pointer-events-none" />
      )}
      {(t === 'source' || t === 'file') && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-700/40 to-zinc-900/40">
          {t === 'source' ? <FileIcon size={44} className="text-fuchsia-300" /> : <FileText size={42} className="text-zinc-300" />}
          <p className="mt-2 text-white text-[11px] font-bold uppercase tracking-wider">{getExt(file.url) || 'File'}</p>
        </div>
      )}
      {/* Bottom name + size strip — Fiverr-style */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0E0E23] via-[#0E0E23]/85 to-transparent pt-6 pb-2 px-3">
        <p className="text-white text-xs font-semibold truncate">{file.name}</p>
        {size && <p className="text-zinc-400 text-[10px] mt-0.5">{size}</p>}
      </div>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DownloadMenu url={file.url} filename={file.name} compact />
      </div>
    </div>
  );
}

function Lightbox({ files, index, onClose, onPrev, onNext }) {
  const file = files[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);
  if (!file) return null;
  const t = typeOf(file.url);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-md flex flex-col"
      onClick={onClose}
      data-testid="file-lightbox"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#0E0E23]/80" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-fuchsia-300/80 font-bold">{index + 1} of {files.length}</p>
          <p className="text-white text-sm font-semibold truncate">{file.name}</p>
        </div>
        <DownloadMenu url={file.url} filename={file.name} />
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          aria-label="Close"
          data-testid="file-lightbox-close"
        >
          <X size={18} />
        </button>
      </div>
      {/* Body */}
      <div className="flex-1 flex items-center justify-center relative px-2 sm:px-12 overflow-hidden" onClick={onClose}>
        {files.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              className="absolute left-2 sm:left-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
              aria-label="Previous"
            ><ChevronLeft size={20} /></button>
            <button
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              className="absolute right-2 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
              aria-label="Next"
            ><ChevronRight size={20} /></button>
          </>
        )}
        <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full flex items-center justify-center">
          {t === 'image' && (
            <img src={file.url} alt={file.name} className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7)]" />
          )}
          {t === 'video' && (
            <video src={file.url} controls autoPlay className="max-w-full max-h-[85vh] rounded-xl" />
          )}
          {t === 'audio' && (
            <div className="bg-[#13153A] border border-white/10 rounded-2xl p-6 sm:p-10 text-center">
              <Music size={56} className="text-fuchsia-300 mx-auto mb-4" />
              <p className="text-white font-bold mb-3">{file.name}</p>
              <audio src={file.url} controls autoPlay className="w-80 max-w-full" />
            </div>
          )}
          {t === 'pdf' && (
            <embed src={`${file.url}#toolbar=1`} type="application/pdf" className="w-[92vw] max-w-5xl h-[85vh] rounded-xl bg-white" />
          )}
          {(t === 'source' || t === 'file') && (
            <div className="bg-[#13153A] border border-white/10 rounded-2xl px-8 py-10 text-center max-w-md">
              <FileIcon size={56} className="text-fuchsia-300 mx-auto mb-3" />
              <p className="text-white font-bold">{file.name}</p>
              <p className="text-zinc-400 text-xs mt-1">{getExt(file.url).toUpperCase() || 'FILE'} · can't preview inline · download to open</p>
              <p className="text-zinc-500 text-[11px] mt-2 italic">Source files like .AI / .PSD / .EPS open best on desktop.</p>
              <a
                href={file.url}
                download={file.name}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-bold text-sm"
              >
                <Download size={14} /> Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Fiverr-style file gallery.
 *
 * @param {Array<string|{url, name, size}>} files  — raw URL strings OR {url, name, size} objs
 * @param {string} label — Optional headline (e.g. "2 files")
 * @param {Array<string|{url, name}>} sourceFiles — Optional separate "Source files" list
 */
export default function FileGallery({ files = [], label, sourceFiles = [] }) {
  const normalized = files.map((f) => {
    if (typeof f === 'string') return { url: resolveUrl(f), name: getFilename(f) };
    return { ...f, url: resolveUrl(f.url), name: f.name || getFilename(f.url) };
  });
  const normSource = sourceFiles.map((f) => {
    if (typeof f === 'string') return { url: resolveUrl(f), name: getFilename(f) };
    return { ...f, url: resolveUrl(f.url), name: f.name || getFilename(f.url) };
  });
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const scrollerRef = useRef(null);

  const downloadAll = async () => {
    // Browsers throttle bulk anchor downloads. Use staggered link clicks.
    normalized.forEach((f, i) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = f.url; a.download = f.name; a.target = '_blank';
        document.body.appendChild(a); a.click(); a.remove();
      }, i * 250);
    });
    if (normalized.length) toast.success(`Downloading ${normalized.length} file${normalized.length === 1 ? '' : 's'}…`);
  };

  if (normalized.length === 0 && normSource.length === 0) return null;

  return (
    <div data-testid="file-gallery">
      {(normalized.length > 0 || label) && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-zinc-300 text-sm font-semibold">{label || `${normalized.length} file${normalized.length === 1 ? '' : 's'}`}</p>
          {normalized.length > 0 && (
            <button
              onClick={downloadAll}
              className="inline-flex items-center gap-1.5 text-zinc-200 hover:text-white text-xs font-semibold underline underline-offset-4 decoration-zinc-500 hover:decoration-white"
              data-testid="download-all-btn"
            >
              <Download size={13} /> Download all
            </button>
          )}
        </div>
      )}

      {/* Horizontal carousel — overflow-x scroll on mobile, snap on each tile */}
      {normalized.length > 0 && (
        <div className="relative">
          <div
            ref={scrollerRef}
            className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-white/10"
            style={{ scrollbarWidth: 'thin' }}
          >
            {normalized.map((f, i) => (
              <div key={i} className="snap-start">
                <Thumbnail file={f} onClick={() => setLightboxIdx(i)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Optional source-files block */}
      {normSource.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-2">Source files</p>
          <ul className="space-y-1.5">
            {normSource.map((f, i) => (
              <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-all">
                <a href={f.url} target="_blank" rel="noreferrer" className="text-white text-sm underline decoration-white/30 hover:decoration-white truncate flex-1">{f.name}</a>
                <DownloadMenu url={f.url} filename={f.name} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {lightboxIdx !== null && (
        <Lightbox
          files={normalized}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + normalized.length) % normalized.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % normalized.length)}
        />
      )}
    </div>
  );
}
